<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { ScoreEntry } from '../lib/types'
import { historyDate, historyTestDate, formatHistoryScore } from '../lib/historyHelpers'

const props = withDefaults(defineProps<{
  entries:   readonly ScoreEntry[]
  paginate?: boolean
  pageSize?: number
}>(), { paginate: false, pageSize: 5 })

const emit = defineEmits<{ delete: [id: number] }>()

const currentPage = ref(0)

const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.entries.length / props.pageSize))
)

const displayedEntries = computed(() => {
  if (!props.paginate) return props.entries
  const start = currentPage.value * props.pageSize
  return props.entries.slice(start, start + props.pageSize)
})

// Debounced reset mirrors UFPMSidebar behavior
let resetTimer: ReturnType<typeof setTimeout> | null = null
watch(() => props.entries, () => {
  if (resetTimer) clearTimeout(resetTimer)
  resetTimer = setTimeout(() => { currentPage.value = 0 }, 150)
})

// Clamp when totalPages shrinks (e.g. delete last item on a page)
watch(totalPages, pages => {
  if (currentPage.value >= pages) currentPage.value = Math.max(0, pages - 1)
})

function catColor(composite: string | number) {
  const v = parseFloat(String(composite))
  if (v >= 90) return { bg: '#1a2e1a', border: '#2ecc71', text: '#2ecc71' }
  if (v >= 75) return { bg: '#2a2000', border: '#a07840', text: '#a07840' }
  return { bg: '#2a0a0a', border: '#e74c3c', text: '#e74c3c' }
}
</script>

<template>
  <div>
    <div v-for="h in displayedEntries" :key="h.id" class="history-item">
      <div style="flex:1;">
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:5px;">
          <span style="font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:28px;color:var(--fg);line-height:1;">
            {{ formatHistoryScore(h.composite) || '-' }}
          </span>
          <span
            class="tag"
            :style="{
              background: catColor(h.composite).bg,
              borderColor: catColor(h.composite).border,
              color: catColor(h.composite).text
            }"
          >{{ h.cat }}</span>
        </div>
        <div style="font-size:11px;color:var(--muted);line-height:1.8;">
          <template v-if="h.ufpmMemberName">
            <span style="color:var(--text);">Member:</span> {{ h.ufpmMemberName }}<br/>
          </template>
          <span style="color:var(--text);">Recorded:</span> {{ historyDate(h) || '-' }}
          <template v-if="historyTestDate(h)">
            &nbsp;| <span style="color:var(--text);">Target Test Date:</span> {{ historyTestDate(h) }}
          </template>
          <br/>
          <span style="color:var(--muted);">
            Age {{ h.age }} | {{ h.sex }} |
            PU: {{ h.puEx ? 'EXM' : (formatHistoryScore(h.puScore) || '-') }} |
            Core: {{ h.coreEx ? 'EXM' : (formatHistoryScore(h.coreScore) || '-') }} |
            Cardio: {{ h.cardioEx ? 'EXM' : (formatHistoryScore(h.cardioScore) || '-') }} |
            WHtR: {{ h.whtrEx ? 'EXM' : (formatHistoryScore(h.whtrScore) || '-') }}
          </span>
        </div>
      </div>
      <button
        class="btn btn-danger"
        style="margin-left:12px;align-self:center;"
        @click="emit('delete', h.id)"
      >X</button>
    </div>

    <div v-if="paginate && totalPages > 1" class="history-pagination">
      <button
        class="btn btn-secondary history-page-btn"
        :disabled="currentPage === 0"
        @click="currentPage--"
      >Prev</button>
      <span class="history-page-indicator">
        {{ currentPage * pageSize + 1 }}-{{ Math.min((currentPage + 1) * pageSize, entries.length) }}
        of {{ entries.length }}
      </span>
      <button
        class="btn btn-secondary history-page-btn"
        :disabled="currentPage >= totalPages - 1"
        @click="currentPage++"
      >Next</button>
    </div>
  </div>
</template>
